<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg face-detection-sidebar"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="
          d-flex
          justify-content-between
          align-items-center
          content-sidebar-header
          px-2
          py-1
        "
      >
        <h5 class="mb-0 text-capitalize">
          {{ isAdd ? `${$t("actions.add")} ${$t("ntechlab.face.title")}` :`${$t("actions.edit")} ${$t("ntechlab.face.title")}` }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver" v-if="!isAdd">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="() => handleSubmit(onSubmit)()"
          @reset.prevent="resetForm"
        >
          <!-- Name -->
          <validation-provider
            #default="validationContext"
            name="name"
            :rules="validationRules.name"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.face.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Active -->
          <validation-provider
            #default="validationContext"
            name="active"
            :rules="validationRules.active"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.face.active')"
              label-for="active"
            >
              <b-form-checkbox
                id="active"
                v-model="itemData.active"
                :state="getValidationState(validationContext)"
                switch
                :value="true"
                :unchecked-value="false"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Comment -->
          <validation-provider
            #default="validationContext"
            name="comment"
            :rules="validationRules.comment"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.face.comment')"
              label-for="comment"
            >
              <b-form-input
                id="comment"
                v-model="itemData.comment"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Watchlist -->
          <validation-provider
            #default="validationContext"
            name="watchlist"
            :rules="validationRules.watchlist"
          >
            <b-form-group
              :label="$tc('ntechlab.face.watch_list', 2)"
              label-for="watchlist"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.watchlist_id"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="watchlistOptions"
                :reduce="(val) => val.value"
                label="label"
                input-id="watchlist"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Upload Image -->
          <validation-provider
            #default="validationContext"
            name="image_url"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.face.image')"
              label-for="image"
            >
              <div class="custom-file-upload">
                <b-form-file
                  id="image"
                  v-model="imageFile"
                  :state="getValidationState(validationContext)"
                  accept="image/*"
                  @change="handleImageUpload"
                  placeholder="Choose a file or drop it here..."
                  drop-placeholder="Drop file here..."
                  browse-text="Browse"
                  class="tall-file-input"
                />
              </div>
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Display detected faces -->
          <div v-if="detectedFaces.length > 0" class="mt-4">
            <div v-for="(face, index) in detectedFaces" :key="face.id" class="face-card mb-4 p-3 border rounded">
              <div class="d-flex align-items-start">
                <!-- Face image -->
                <div class="face-image mr-3 position-relative" style="width: 150px;">
                  <img :src="face.image" class="img-fluid rounded" />
                  <div v-if="face.isAdded" class="registered-label">
                    {{$t('ntechlab.face.face_added_label')}}
                  </div>
                </div>

                <!-- Face details -->
                <div class="flex-grow-1">
                  <!-- Name -->
                  <validation-provider
                    #default="validationContext"
                    name="name"
                    rules="required"
                  >
                    <b-form-group
                      label="Nombre"
                      label-for="name"
                    >
                      <b-form-input
                        v-model="face.name"
                        id="name"
                        :state="getValidationState(validationContext)"
                        placeholder="Enter name"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] || $t('validation.required', { field: 'Name' }) }}
                      </b-form-invalid-feedback>
                      <small v-if="face.isAdded" class="text-success d-block mt-1">
                        <i class="feather icon-check mr-1"></i>{{$t('ntechlab.face.already_face_added')}}
                      </small>
                    </b-form-group>
                  </validation-provider>

                  <!-- Comment -->
                  <validation-provider
                    #default="validationContext"
                    name="comment"
                    rules="required"
                  >
                    <b-form-group
                      label="Comentario"
                      label-for="comment"
                    >
                      <b-form-input
                        v-model="face.comment"
                        id="comment"
                        :state="getValidationState(validationContext)"
                        placeholder="Enter comment"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] || $t('validation.required', { field: 'Comment' }) }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>

                  <!-- Watchlist -->
                  <validation-provider
                    #default="validationContext"
                    name="watchlist"
                    rules="required"
                  >
                    <b-form-group
                      :label="$tc('ntechlab.face.watch_list', 2)"
                      label-for="watchlist"
                    >
                      <v-select
                        v-model="face.watchlist_id"
                        :options="watchlistOptions"
                        :reduce="(val) => val.value"
                        :state="getValidationState(validationContext)"
                        label="label"
                        input-id="watchlist"
                      />
                      <b-form-invalid-feedback :state="getValidationState(validationContext)">
                        {{ validationContext.errors[0] || $t('validation.required', { field: 'Watchlist' }) }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </div>

                <!-- Delete button -->
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="ml-2"
                  @click="removeFace(index)"
                >
                  <feather-icon icon="XIcon" size="16" />
                </b-button>
              </div>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              @click.prevent="validateAndSubmit"
              :disabled="isLoading || (isAdd && !hasAddableFaces)"
            >
              <b-spinner
                small
                v-if="isLoading"
                class="mr-1"
              />
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
              :disabled="isLoading"
            >
             {{$t('actions.cancel')}}
            </b-button>
          </div>
        </b-form>
      </validation-observer>

      <!-- Simple form for isAdd=true -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver" v-else>
        <b-form
          class="p-2"
          @submit.prevent="() => handleSubmit(onSubmit)()"
          @reset.prevent="resetForm"
        >
          <!-- Upload Image -->
          <validation-provider
            #default="validationContext"
            name="image_url"
            rules="required"
            v-if="detectedFaces.length === 0"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.face.image')"
              label-for="image"
            >
              <div class="custom-file-upload">
                <b-form-file
                  id="image"
                  v-model="imageFile"
                  :state="getValidationState(validationContext)"
                  accept="image/*"
                  @change="handleImageUpload"
                  placeholder="Choose a file or drop it here..."
                  drop-placeholder="Drop file here..."
                  browse-text="Browse"
                  class="tall-file-input"
                />
              </div>
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Display detected faces -->
          <div v-if="detectedFaces.length > 0" class="mt-4">
            <div
              class="d-flex justify-content-center img-bbox-container position-relative"
              :style="{
                backgroundImage: imageWithBbox ? `url(${imageWithBbox})` : 'none',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }"
            >
              <!-- Clear image button -->
              <b-button
                variant="danger"
                size="sm"
                class="position-absolute clear-image-btn"
                @click="clearImageAndFaces"
              >
                <feather-icon icon="XIcon" size="16" />
              </b-button>
            </div>
            <div v-for="(face, index) in detectedFaces" :key="face.id" class="face-card mb-4 mt-4 p-3 border rounded">
              <div class="d-flex align-items-start">
                <!-- Face image -->
                <div class="face-image mr-3 position-relative" style="width: 150px;">
                  <img :src="face.image" class="img-fluid rounded" />
                  <div v-if="face.isAdded" class="registered-label">
                    {{$t('ntechlab.face.face_added_label')}}
                  </div>
                </div>

                <!-- Face details -->
                <div class="flex-grow-1">
                  <!-- Name -->
                  <validation-provider
                    #default="validationContext"
                    name="name"
                    rules="required"
                  >
                    <b-form-group
                      label="Nombre"
                      label-for="name"
                    >
                      <b-form-input
                        v-model="face.name"
                        id="name"
                        :state="getValidationState(validationContext)"
                        placeholder="Enter name"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] || $t('validation.required', { field: 'Name' }) }}
                      </b-form-invalid-feedback>
                      <small v-if="face.isAdded" class="text-success d-block mt-1">
                        <i class="feather icon-check mr-1"></i>{{$t('ntechlab.face.already_face_added')}}
                      </small>
                    </b-form-group>
                  </validation-provider>

                  <!-- Comment -->
                  <validation-provider
                    #default="validationContext"
                    name="comment"
                    rules="required"
                  >
                    <b-form-group
                      label="Comentario"
                      label-for="comment"
                    >
                      <b-form-input
                        v-model="face.comment"
                        id="comment"
                        :state="getValidationState(validationContext)"
                        placeholder="Enter comment"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] || $t('validation.required', { field: 'Comment' }) }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>

                  <!-- Watchlist -->
                  <validation-provider
                    #default="validationContext"
                    name="watchlist"
                    rules="required"
                  >
                    <b-form-group
                      :label="$tc('ntechlab.face.watch_list', 2)"
                      label-for="watchlist"
                    >
                      <v-select
                        v-model="face.watchlist_id"
                        :options="watchlistOptions"
                        :reduce="(val) => val.value"
                        :state="getValidationState(validationContext)"
                        label="label"
                        input-id="watchlist"
                      />
                      <b-form-invalid-feedback :state="getValidationState(validationContext)">
                        {{ validationContext.errors[0] || $t('validation.required', { field: 'Watchlist' }) }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </div>

                <!-- Delete button -->
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="ml-2"
                  @click="removeFace(index)"
                >
                  <feather-icon icon="XIcon" size="16" />
                </b-button>
              </div>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
              :disabled="isLoading || (isAdd && !hasAddableFaces)"

            >
              <b-spinner
                small
                v-if="isLoading"
                class="mr-1"
              />
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
              :disabled="isLoading"
            >
             {{$t('actions.cancel')}}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BFormFile,
  BSpinner,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, watch, computed } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { extend } from 'vee-validate';

extend('requiredArray', {
  validate(value) {
    return Array.isArray(value) && value.length > 0;
  },
  message: 'El campo {_field_} es obligatorio'
});

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    BFormFile,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    watchlistOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    snapshotData: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const imageFile = ref(null);
    const isLoading = ref(false);
    const detectedFaces = ref([]);
    const { t } = useI18nUtils();
    const toast = useToast();
    const imageWithBbox = ref(null);
    const imageBboxClass = ref('');

    // Add watcher for imageFile
    watch(imageFile, async (newFile) => {
      if (newFile && props.isAdd) {
        isLoading.value = true;
        try {
          const formData = new FormData();
          formData.append('photo', newFile);

          const response = await store.dispatch('ntechlab_face/uploadImage', formData);
          if (response.success) {
            // Process detected faces
            // Extract image_with_bbox from response data
            imageWithBbox.value = response.data.image_with_bbox;

            // Get face objects (exclude image_with_bbox key)
            const faces = Object.keys(response.data)
              .filter(key => key !== 'image_with_bbox')
              .map(key => response.data[key]);

            detectedFaces.value = faces.map(face => ({
              id: face.id,
              image: face.image,
              name: face.isAdded ? face.name : '',
              comment: face.isAdded ? face.comment : '',
              watchlist_id: face.isAdded ? face.watchlist_id : null,
              features: face.features,
              bbox: face.bbox,
              isAdded: face.isAdded
            }));

            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message || 'Error uploading image',
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        } catch (error) {
          if(error.response.status === 404){
            toast({
              component: ToastificationContent,
              props: {
                title: error.response?.data?.message || 'Error uploading image',
                icon: "AlertTriangleIcon",
                variant: "warning",
              },
            });
            resetForm();
            emit('update:is-sidebar-active', false);
            return;
          }else{
            toast({
              component: ToastificationContent,
              props: {
                title: error.response?.data?.message || 'Error uploading image',
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        } finally {
          isLoading.value = false;
        }
      }
    });

    const blankData = {
      name: null,
      active: null,
      comment: null,
      image_url: null,
      api_server: null,
      watchlist_id: null,
      id: null,
    };

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };
    itemData.value.active = true;

    const removeFace = (index) => {
      detectedFaces.value.splice(index, 1);
    };

    const clearImageAndFaces = () => {
      // Clear detected faces
      detectedFaces.value = [];
      // Clear image file
      imageFile.value = null;
      // Clear image with bbox
      imageWithBbox.value = null;
      // Clear the file input element
      const fileInput = document.getElementById('image');
      if (fileInput) {
        fileInput.value = '';
      }
      // Clear itemData image_url if needed
      itemData.value.image_url = null;
    };

    // Get form validation with resetForm renamed to baseResetForm
    const { refFormObserver, getValidationState, resetForm: baseResetForm } = formValidation(resetData);

    // Create enhanced resetForm function
    function resetForm() {
      baseResetForm();
      imageFile.value = null;
      if (props.isAdd) {
        detectedFaces.value = [];
        // Reset the file input by clearing its value
        const fileInput = document.getElementById('image');
        if (fileInput) {
          fileInput.value = '';
        }
      }
    }

    const loadData = () => {

      if (!props.isAdd && props.itemEdit) {
        itemData.value = {
          id: props.itemEdit.id || null,
          name: props.itemEdit.name || null,
          comment: props.itemEdit.comment || null,
          image_url: props.itemEdit.image_url || null,
          active: props.itemEdit.active !== undefined ? Boolean(props.itemEdit.active) : true,
          watchlist_id: props.itemEdit.watch_lists && props.itemEdit.watch_lists.length > 0
            ? props.itemEdit.watch_lists[0].id
            : props.itemEdit.watch_list_id || null // Try both possible property names
        };

      } else {
        // Reset form when opening in add mode
        resetForm();

        // Check for snapshot data passed from camera widget
        if (props.isAdd && props.snapshotData && props.snapshotData.success && props.snapshotData.data) {
          // Convert base64 to File object for the form
          const createFileFromBase64 = (base64Data, filename = 'snapshot.jpg') => {
            const arr = base64Data.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);

            while (n--) {
              u8arr[n] = bstr.charCodeAt(n);
            }

            return new File([u8arr], filename, { type: mime });
          };

          try {
            const file = createFileFromBase64(props.snapshotData.data);

            imageFile.value = file;

            itemData.value.image_url = props.snapshotData.data;

            toast({
              component: ToastificationContent,
              props: {
                title: 'Snapshot automatically loaded from camera',
                icon: "CameraIcon",
                variant: "success",
              },
            });


          } catch (error) {
            console.error('Error converting snapshot to file:', error);
            toast({
              component: ToastificationContent,
              props: {
                title: 'Error loading snapshot from camera',
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        }
      }
    };

    const submitForm = async (e) => {

      e.preventDefault();
      const isValid = await refFormObserver.value.validate();
      if (isValid) {
        await onSubmit();
      }
    };

    const onSubmit = async () => {
      isLoading.value = true;
      try {
        let response;

        if (!props.isAdd) {
          const updateData = {
            id: props.itemEdit.id,
            params: {
              name: itemData.value.name,
              comment: itemData.value.comment,
              active: itemData.value.active,
              watchlist_id: itemData.value.watchlist_id
            }
          };
          response = await store.dispatch('ntechlab_face/edit', updateData);
        } else {
          // Handle adding new faces
          const facesToAdd = detectedFaces.value.filter(face =>
            face.name && face.comment && face.watchlist_id
          );

          if (facesToAdd.length === 0) {
            toast({
              component: ToastificationContent,
              props: {
                title: t('ntechlab.face.no_faces_to_add'),
                icon: "AlertTriangleIcon",
                variant: "warning",
              },
            });
            isLoading.value = false;
            return;
          }

          // Create FormData and append the original file
          const formData = new FormData();
          formData.append('photo', imageFile.value);

          // Add faces data without the base64 images
          const facesData = facesToAdd.map(face => ({
            id: face.id,
            name: face.name,
            comment: face.comment,
            watchlist_id: face.watchlist_id,
            features: face.features,
            bbox: face.bbox
          }));

          formData.append('faces', JSON.stringify(facesData));
          response = await store.dispatch('ntechlab_face/add', formData);

          // After successful addition, mark faces as added and clear fields for next addition
          if (response.success) {
            facesToAdd.forEach(addedFace => {
              const faceInList = detectedFaces.value.find(f => f.id === addedFace.id);
              if (faceInList) {
                faceInList.isAdded = true;
                // Clear the fields to prepare for next addition
                faceInList.name = '';
                faceInList.comment = '';
                faceInList.watchlist_id = null;
              }
            });

            // Reset form and close sidebar after successful addition
            resetForm();
            emit('update:is-sidebar-active', false);
          }
        }

        // Show success message
        toast({
          component: ToastificationContent,
          props: {
            title: response.message || (props.isAdd ? t('ntechlab.face.success_adding') : t('ntechlab.face.success_updating')),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        // Emit refresh event to parent
        emit('refetch-data');

        // Only reset form and close sidebar if not in add mode
        if (!props.isAdd) {
          resetForm();
          emit('update:is-sidebar-active', false);
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || t('ntechlab.face.error_saving'),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isLoading.value = false;
      }
    };

    // Modify the computed property for addable faces
    const hasAddableFaces = computed(() => {
      return detectedFaces.value.some(face =>
        face.name && face.comment && face.watchlist_id
      );
    });

    const handleImageUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        // Create a FileReader to read the file
        const reader = new FileReader();
        reader.onload = (e) => {
          itemData.value.image_url = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    };

    // Add a watcher to debug props changes
    watch(() => props.isAdd, (newVal, oldVal) => {

    });

    watch(() => props.itemEdit, (newVal, oldVal) => {

    });

    const validateAndSubmit = async () => {

      try {
        if (refFormObserver.value) {
          const { valid, errors } = await refFormObserver.value.validate();


          // If validation passes or we're in edit mode with valid data
          if (valid || (!props.isAdd && itemData.value.name && itemData.value.comment && itemData.value.watchlist_id)) {

            await onSubmit();
          } else {

            toast({
              component: ToastificationContent,
              props: {
                title: 'Please fill in all required fields',
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        } else {

          // If we're in edit mode and have valid data, proceed anyway
          if (!props.isAdd && itemData.value.name && itemData.value.comment && itemData.value.watchlist_id) {

            await onSubmit();
          }
        }
      } catch (error) {

      }
    };

    // Modify the validation rules for edit mode
    const validationRules = computed(() => ({
      name: { required: true },
      comment: { required: true },
      watchlist: { required: true },
      active: { required: false } // Make active optional since it has a default value
    }));

    // Watch itemEdit changes to reload data
    watch(() => props.itemEdit, (newVal) => {
      if (newVal && !props.isAdd) {
        loadData();
      }
    }, { immediate: true });

    return {
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      imageFile,
      handleImageUpload,
      isLoading,
      detectedFaces,
      removeFace,
      clearImageAndFaces,
      hasAddableFaces,
      submitForm,
      validateAndSubmit,
      validationRules,
      imageWithBbox,
      imageBboxClass,
    };
  },
};
</script>

<style>
.img-bbox-container {
  display: grid;
  place-items: center;
  width: 100%;
  min-height: 40vh;
  max-height: 60vh;
  margin: 0 auto;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.clear-image-btn {
  top: 10px;
  right: 10px;
  z-index: 10;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}


.carrousel-alarm-table .VueCarousel-navigation-prev {
  left: 40px;
}

.carrousel-alarm-table .VueCarousel-navigation-next {
  right: 40px;
}

/* Sidebar width customization */
.face-detection-sidebar {
  width: 600px !important;
  max-width: 80vw !important;
}

/* Dark mode styles */
.dark-layout .face-detection-sidebar {
  background-color: #283046 !important;
}

.dark-layout .face-detection-sidebar .content-sidebar-header {
  background-color: #161d31;
  border-bottom: 1px solid #3b4253;
}

.dark-layout .face-card {
  background: #283046 !important;
  border-color: #3b4253 !important;
}

.dark-layout .custom-file-upload .custom-file-label {
  background-color: #283046;
  border-color: #404656;
  color: #b4b7bd;
}

.dark-layout .custom-file-upload .custom-file-label::after {
  background-color: #161d31;
  border-color: #404656;
  color: #b4b7bd;
}

.dark-layout .form-control {
  background-color: #283046;
  /* border-color: #404656; */
  color: #b4b7bd;
}

.dark-layout .form-control:focus {
  /* background-color: var(--third-dark) !important; */
  border-color: #283046;
  color: #b4b7bd;
}

.dark-layout .vs__dropdown-toggle {
  background-color: var(--second-dark) !important;
  /* border-color: #404656; */
  color: #b4b7bd;
}

.dark-layout .vs__selected {
  color: #b4b7bd;
}

.dark-layout .vs__search::placeholder {
  color: #676d7d;
}

.dark-layout .vs__dropdown-menu {
  background-color: var(--main-dark) !important;
  /* border-color: #404656; */
}

.dark-layout .vs__dropdown-option {
  color: #b4b7bd;
}

.dark-layout .vs__dropdown-option--highlight {
  color: #fff;
}

.custom-file-upload {
  position: relative;
  margin-bottom: 1rem;
}

.custom-file-upload .custom-file {
  height: auto;
}

.tall-file-input {
  min-height: 200px !important;
}

.tall-file-input .custom-file-label {
  height: 200px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  border: 2px dashed #404656;
  border-radius: 8px;
}

.tall-file-input .custom-file-label::after {
  height: 100%;
  display: flex;
  align-items: center;
  content: "Browse";
  border-left: 1px solid #404656;
}

.tall-file-input:hover .custom-file-label {
  border-color: #13293e;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
}

.face-card {
  background: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.face-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.face-image {
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.face-image img {
  width: 100%;
  height: auto;
  object-fit: cover;
}

.registered-label {
  position: absolute;
  top: 20px;
  right: -35px;
  background: rgba(40, 199, 111, 0.9);
  color: white;
  padding: 5px 40px;
  transform: rotate(45deg);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  z-index: 1;
}
</style>